/**
 * ArchiveRecords — archive retrieval search (drafts/57 ARC-03).
 * Filter the archive by text / type / status / confidentiality / legal-hold;
 * rows open the cover-sheet detail. "+ New intake" opens the cover-sheet form.
 * Route: /archive/records.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Card, CardBody, PageHero, Button } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import { ArchiveIntakeModal } from './ArchiveIntakeModal';
import {
  ARCHIVE_RECORD_TYPES, ARCHIVE_STATUSES, CONFIDENTIALITIES,
  STATUS_STYLE, RISK_STYLE, fmtDate, type ArchiveRecordsResponse,
} from './types';

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-subtle, #d1d5db)', fontSize: 14,
};

export function ArchiveRecords() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Drill-down filters seeded from the dashboard stat-cards (?status / ?legal_hold
  // / ?record_type). Subsequent edits are local to this screen.
  const search = useSearch({ strict: false }) as { status?: string; legal_hold?: string; record_type?: string };
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [q, setQ] = useState('');
  const [recordType, setRecordType] = useState(search.record_type ?? '');
  const [status, setStatus] = useState(search.status ?? '');
  const [confidentiality, setConfidentiality] = useState('');
  const [legalHold, setLegalHold] = useState(search.legal_hold ?? '');
  const anyFilter = !!(q || recordType || status || confidentiality || legalHold);
  const clearFilters = () => { setQ(''); setRecordType(''); setStatus(''); setConfidentiality(''); setLegalHold(''); };

  const query = useQuery<ArchiveRecordsResponse>({
    queryKey: ['archive', 'records', { q, recordType, status, confidentiality, legalHold }],
    queryFn: ({ signal }) => apiFetch<ArchiveRecordsResponse>('/api/v1/archive/records', {
      signal,
      query: {
        q: q.trim() || undefined,
        record_type: recordType || undefined,
        status: status || undefined,
        confidentiality: confidentiality || undefined,
        legal_hold: legalHold || undefined,
      },
    }),
    staleTime: 15_000,
  });

  const open = (id: string) => navigate({ to: '/archive/records/$id', params: { id } });

  return (
    <div style={{ padding: 'var(--space-6, 24px)', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHero
        eyebrow={t('archive.records.eyebrow', { defaultValue: 'Archive & Records' })}
        title={t('archive.records.title', { defaultValue: 'Archive Records' })}
        subtitle={t('archive.records.subtitle', { defaultValue: 'Retrieve archived GMP records by type, status, confidentiality or legal hold.' })}
        actions={
          <Button variant="secondary" size="lg" leadingIcon="+" onClick={() => setIntakeOpen(true)}>
            {t('archive.records.newIntake', { defaultValue: 'New intake' })}
          </Button>
        }
      />

      <Card>
        <CardBody>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 240px' }}>
              <label style={{ fontSize: 12, color: '#6B7989' }} htmlFor="arc-q">{t('archive.records.search', { defaultValue: 'Search title / doc # / source' })}</label>
              <input id="arc-q" style={inputStyle} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Paracetamol, SOP-QA-002" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: '#6B7989' }} htmlFor="arc-f-type">{t('archive.records.type', { defaultValue: 'Type' })}</label>
              <select id="arc-f-type" style={inputStyle} value={recordType} onChange={(e) => setRecordType(e.target.value)}>
                <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                {ARCHIVE_RECORD_TYPES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: '#6B7989' }} htmlFor="arc-f-status">{t('archive.records.status', { defaultValue: 'Status' })}</label>
              <select id="arc-f-status" style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                {ARCHIVE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: '#6B7989' }} htmlFor="arc-f-conf">{t('archive.records.conf', { defaultValue: 'Confidentiality' })}</label>
              <select id="arc-f-conf" style={inputStyle} value={confidentiality} onChange={(e) => setConfidentiality(e.target.value)}>
                <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                {CONFIDENTIALITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: '#6B7989' }} htmlFor="arc-f-hold">{t('archive.records.legalHold', { defaultValue: 'Legal hold' })}</label>
              <select id="arc-f-hold" style={inputStyle} value={legalHold} onChange={(e) => setLegalHold(e.target.value)}>
                <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                <option value="true">{t('common.yes', { defaultValue: 'Yes' })}</option>
                <option value="false">{t('common.no', { defaultValue: 'No' })}</option>
              </select>
            </div>
            {anyFilter && (
              <button type="button" onClick={clearFilters} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border-subtle, #d1d5db)', background: 'white', cursor: 'pointer', fontWeight: 600, color: '#475569' }}>
                {t('archive.records.clear', { defaultValue: 'Clear filters' })}
              </button>
            )}
          </div>
        </CardBody>
      </Card>

      {query.isLoading && <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
      {query.isError && <p role="alert" style={{ color: '#B91C1C' }}>{t('archive.records.error', { defaultValue: 'Could not load archive records.' })}</p>}

      {query.data && (
        <Card>
          <CardBody>
            <div style={{ marginBottom: 8, color: '#6B7989', fontSize: 13 }}>
              {t('archive.records.count', { defaultValue: '{{n}} record(s)', n: query.data.total })}
            </div>
            {query.data.items.length === 0 ? (
              <p style={{ color: '#6B7989' }}>{t('archive.records.empty', { defaultValue: 'No records match these filters.' })}</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6B7989', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '8px 10px' }}>{t('archive.records.col.record', { defaultValue: 'Record' })}</th>
                    <th style={{ padding: '8px 10px' }}>{t('archive.records.col.type', { defaultValue: 'Type' })}</th>
                    <th style={{ padding: '8px 10px' }}>{t('archive.records.col.dept', { defaultValue: 'Dept / Site' })}</th>
                    <th style={{ padding: '8px 10px' }}>{t('archive.records.col.risk', { defaultValue: 'Risk' })}</th>
                    <th style={{ padding: '8px 10px' }}>{t('archive.records.col.retention', { defaultValue: 'Retention end' })}</th>
                    <th style={{ padding: '8px 10px' }}>{t('archive.records.col.status', { defaultValue: 'Status' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((r) => {
                    const s = STATUS_STYLE[r.status];
                    return (
                      <tr key={r.archive_record_id} onClick={() => open(r.archive_record_id)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                        <td style={{ padding: '10px' }}>
                          <strong>{r.title}</strong>
                          <div style={{ color: '#6B7989', fontSize: 12 }}>{r.document_number ?? r.source_system ?? '—'}</div>
                        </td>
                        <td style={{ padding: '10px' }}>{r.record_type}</td>
                        <td style={{ padding: '10px' }}>
                          {r.department_name ?? '—'}
                          <div style={{ color: '#6B7989', fontSize: 12 }}>{r.site_name ?? '—'}</div>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ background: RISK_STYLE[r.risk_class].bg, color: RISK_STYLE[r.risk_class].fg, borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{r.risk_class}</span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          {fmtDate(r.retention_end_at, i18n.language)}
                          {r.legal_hold_active && <span title={r.legal_hold_ref ?? ''} style={{ marginLeft: 6 }}>🔒</span>}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ background: s.bg, color: s.fg, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{s.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {intakeOpen && (
        <ArchiveIntakeModal
          onClose={() => setIntakeOpen(false)}
          onCreated={(rec) => { setIntakeOpen(false); open(rec.archive_record_id); }}
        />
      )}
    </div>
  );
}
